/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState, useEffect } from "react";
import { Form, InputGroup, Row, Image, Col, Card } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { AiFillCloseCircle } from "react-icons/ai";

import * as S from "../../styles";
import * as LS from "./styles";

export interface IImageDropZoneProps {
    id: string;
    label?: string;
    labelColor?: string;
    errorText?: string;
    value?: any;
    required?: boolean;
    placeholder?: string;
    onCompChange?(id: string, payload: any): void;
    maxFiles: number;
    maxSize?: number;
    hasError?: boolean;
}

const formatSize = (bytes: number) => {
    if (bytes >= 1048576) {
        return (bytes / 1048576).toFixed(0) + " MB";
    }
    return (bytes / 1024).toFixed(0) + " KB";
};

export const ImageDropZone = ({
    id,
    label,
    labelColor,
    errorText,
    value,
    required,
    placeholder,
    onCompChange,
    maxFiles,
    maxSize,
    hasError,
}: IImageDropZoneProps) => {
    const [files, setFiles] = useState(value ?? []);
    const [validSize, setValidSize] = useState(true);
    const [isInvalid, setInvalid] = useState(false);

    useEffect(() => {
        if (hasError) {
            setInvalid(true);
        }
    }, [hasError]);
    useEffect(() => {
        onCompChange && onCompChange(id, value);
    }, [value]);
    const { getRootProps, getInputProps } = useDropzone({
        // maxSize: maxSize,
        maxFiles: maxFiles,
        multiple: maxFiles > 1,
        accept: {
            "image/png": [".png"],
            "image/jpg": [".jpg"],
            "image/jpeg": [".jpeg"],
            "image/gif": [".gif"],
        },
        onDrop: (droppedFiles) => {
            const validDroppedFiles = [];
            let hasOversized = false;

            for (let i = 0; i < droppedFiles.length; i++) {
                let file = droppedFiles[i];
                console.log("Image size (bytes):", file.size);
                if (file.size > (maxSize || 2097152)) {
                    hasOversized = true;
                } else {
                    validDroppedFiles.push(file);
                }
            }

            if (hasOversized) {
                setValidSize(false);
            } else {
                setValidSize(true);
            }

            if (validDroppedFiles.length === 0) {
                return;
            }

            const allFiles: any = [];
            for (let i = 0; i < validDroppedFiles.length; i++) {
                let file = validDroppedFiles[i];

                // Make new FileReader
                let reader = new FileReader();

                // Convert the file to base64 text
                reader.readAsDataURL(file);

                reader.onload = () => {
                    // Make a fileInfo Object
                    let fileInfo = {
                        name: file.name,
                        type: file.type,
                        size: Math.round(file.size / 1000) + " kB",
                        base64: reader.result,
                        file: file,
                    };

                    // Push it to the state
                    allFiles.push(fileInfo);

                    // If all valid files have been processed
                    if (allFiles.length == validDroppedFiles.length) {
                        onCompChange && onCompChange(id, allFiles);
                        setFiles(allFiles);
                    }
                }; // reader.onload
            } // for
        },
    });

    const removeSelection = (index: number) => {
        if (index > -1) {
            const _files = [...files];
            if (_files[index] !== undefined) {
                _files.splice(index, 1);
            }
            setFiles(_files);
            onCompChange && onCompChange(id, _files);
            setValidSize(true);
        }
    };

    const DropzoneFile = (props: any) => {
        const { name, base64, fileIndex } = props;

        return (
            <Col xs={6} className="dropzone-preview mt-2">
                <div style={LS.DropZoneFileContainer}>
                    <Image
                        src={base64}
                        className="dropzone-image"
                        resizeMode={"contain"}
                    />
                    <AiFillCloseCircle
                        size={22}
                        onClick={removeSelection.bind(null, fileIndex)}
                        style={LS.DropZoneFileRemoveIcon}
                    />
                </div>
                <Card.Text className="dropzone-filename">{name}</Card.Text>
            </Col>
        );
    };

    const _renderLabelIf = () => {
        if (label) {
            return (
                <S.FormLabel color={labelColor}>
                    {label}
                    {required && <sup style={{ color: "red" }}>*</sup>}
                </S.FormLabel>
            );
        }
        return null;
    };
    const _renderErrorTextIf = () => {
        if (hasError && errorText) {
            return (
                <S.FeedbackOtherLabel type="tooltip">
                    {errorText}
                </S.FeedbackOtherLabel>
            );
        }
        return null;
    };

    const tooltip =
        maxFiles === 1
            ? `${maxFiles} image is the maximum number of images you can drop here`
            : `${maxFiles} images are the maximum number of images you can drop here`;
    return (
        <>
            <Form.Group id={id} className="formgroup">
                {_renderLabelIf()}
                <InputGroup
                    {...getRootProps({
                        className:
                            "dropzone rounded d-flex align-items-center justify-content-center mb-4",
                    })}
                >
                    <Form.Control
                        className="formcontrol shadow-none"
                        {...getInputProps()}
                        isInvalid={isInvalid}
                    />
                    <div className="dz-default dz-message text-center">
                        <p
                            className="dz-button mb-0"
                            style={{ fontSize: "14px" }}
                        >
                            Drop images here to upload
                        </p>
                        <em
                            style={{
                                fontSize: "14px",
                                paddingLeft: "2px",
                                paddingRight: "2px",
                            }}
                        >
                            ({tooltip})
                        </em>
                    </div>
                    <InputGroup>{_renderErrorTextIf()}</InputGroup>
                </InputGroup>
            </Form.Group>
            <Row className="dropzone-files">
                {!validSize ? (
                    <span style={{ color: "red" }}>
                        {`Image size is too big. Maximum allowed is ${formatSize(
                            maxSize
                        )}`}
                    </span>
                ) : null}
                {files?.map((file: any, index: number) => (
                    <DropzoneFile key={file.name} {...file} fileIndex={index} />
                ))}
            </Row>
        </>
    );
};

ImageDropZone.defaultProps = {
    hasError: false,
    required: false,
    maxFiles: 5,
    maxSize: 2097152,
};
